import React, { useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { SelectV4 } from '@labrav/react-components'
import type { FormField } from '@labrav/react-components/lib/@types/types/forms'
import { FormikHandlers } from 'formik'
import { industries as industryOptions } from './industries'

const industrySelectOptions = industryOptions.map(option => ({
  label: option.name,
  value: option.value,
}))

const industryFieldBase: FormField = {
  title: 'Industry',
  humanReadableKey: 'industry',
  id: 'industry',
  isRequired: false,
  isReadOnly: false,
  isCustom: false,
  dataType: 'enum',
  placeHolder: '',
  options: industrySelectOptions,
}

const closedSelectMenuProps = { transitionDuration: 0 }

type CompanyIndustrySelectProps = {
  industry: string
  onIndustryChange: (industry: string) => void
  onBlur: FormikHandlers['handleBlur']
  isError: boolean
  errorText: string
}

export const CompanyIndustrySelect: React.FC<CompanyIndustrySelectProps> = ({
  industry,
  onIndustryChange,
  onBlur,
  isError,
  errorText,
}) => {
  const [selectedIndustry, setSelectedIndustry] = useState(industry)
  const hasUserSelectedIndustry = useRef(false)

  useEffect(() => {
    if (hasUserSelectedIndustry.current) {
      return
    }
    if (!industry || industry === selectedIndustry) {
      return
    }
    setSelectedIndustry(industry)
  }, [industry, selectedIndustry])

  const field = useMemo<FormField>(
    () =>
      selectedIndustry
        ? { ...industryFieldBase, value: selectedIndustry }
        : industryFieldBase,
    [selectedIndustry]
  )

  const handleIndustryChange: React.ComponentProps<
    typeof SelectV4
  >['onChange'] = event => {
    const nextIndustry = event.target.value
    hasUserSelectedIndustry.current = true
    flushSync(() => {
      setSelectedIndustry(nextIndustry)
    })
    onIndustryChange(nextIndustry)
  }

  return (
    <SelectV4
      field={field}
      value={selectedIndustry}
      onChange={handleIndustryChange}
      onBlur={onBlur}
      isError={isError}
      errorText={errorText}
      MenuProps={closedSelectMenuProps}
    />
  )
}
